import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';
import { LoginService } from 'app/services/auth/login.service';
import { AuthenticationService } from 'app/auth/service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RegisterComponent implements OnInit, OnDestroy {
  displayPassword = 'password';
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = '';
  data = {
    nom: '',
    prenom: '',
    email: '',
    password: '',
  };
  dataError = {
    nom: '',
    prenom: '',
    email: '',
    password: '',
  };
  errorMessage = '';
  public passwordTextType: boolean;

  private _unsubscribeAll: Subject<void>;

  constructor(
    private loginService: LoginService,
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _logicielCaisseAuth: LogicielCaisseAuthService
  ) {
    this._unsubscribeAll = new Subject();
    this._coreConfigService.config = {
      layout: {
        navbar: { hidden: true },
        menu: { hidden: true },
        footer: { hidden: true },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  compte(): void {
    this.errorMessage = '';
    let hasError = false;

    const nom = String(this.data.nom || '').trim();
    const prenom = String(this.data.prenom || '').trim();
    const email = String(this.data.email || '').trim().toLowerCase();
    const password = String(this.data.password || '');

    if (nom.length < 1 || nom.length > 35) {
      this.dataError.nom = 'Le nom doit contenir entre 1 et 35 caractères.';
      hasError = true;
    } else {
      this.dataError.nom = '';
    }

    if (prenom.length < 1 || prenom.length > 35) {
      this.dataError.prenom = 'Le prénom doit contenir entre 1 et 35 caractères.';
      hasError = true;
    } else {
      this.dataError.prenom = '';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.dataError.email = 'Email invalide.';
      hasError = true;
    } else {
      this.dataError.email = '';
    }

    if (password.length < 6) {
      this.dataError.password = 'Le mot de passe doit contenir au moins 6 caractères.';
      hasError = true;
    } else {
      this.dataError.password = '';
    }

    if (hasError) {
      return;
    }

    const dataSend = {
      first_name: prenom,
      last_name: nom,
      email,
      password,
    };

    this.loading = true;
    this.loginService.create(dataSend).subscribe({
      next: (data) => {
        if (data?.message === 1) {
          this.dataError.email = 'Email déjà utilisé !';
          this.loading = false;
          return;
        }

        if (data?.error) {
          this.errorMessage = String(data.error);
          this.loading = false;
          return;
        }

        if (data?.user && data.emailSent !== false) {
          sessionStorage.setItem('registration_onboarding_password', password);
          this.persistRegisteredUser(data);
          void this._router.navigate(['/token']);
        } else {
          this.errorMessage = 'Réponse serveur inattendue.';
        }
        this.loading = false;
      },
      error: (err) => {
        const detail = err?.error?.detail ? ` (${err.error.detail})` : '';
        this.errorMessage =
          (err?.error?.error ||
            err?.error?.message ||
            'Inscription impossible. Vérifiez vos informations et réessayez.') + detail;
        this.loading = false;
      },
    });
  }

  private persistRegisteredUser(data: { id?: number; user: Record<string, unknown> }): void {
    const raw = { ...(data.user || {}) };
    delete raw.password;
    delete raw.token;

    const sessionUser = {
      ...raw,
      is_active: raw.is_active ?? false,
      stations: Array.isArray(raw.stations) ? raw.stations : [],
    };

    this._logicielCaisseAuth.clearSession();
    this._logicielCaisseAuth.invalidateAccessCache();
    localStorage.removeItem('erp_token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');

    localStorage.setItem('users', JSON.stringify(sessionUser));
    localStorage.setItem('currentUser', JSON.stringify(sessionUser));
    this._authenticationService.applyLoginSession({ user: sessionUser });
  }

  ngOnInit(): void {
    if ((environment as { currentModule?: string }).currentModule !== 'hub') {
      const hub = ((environment as { hubUrl?: string }).hubUrl || 'https://front-hub.agrosilo-solutions.com').replace(/\/$/, '');
      window.location.href = `${hub}/register`;
      return;
    }

    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/';

    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  changeDisplay(): void {
    this.displayPassword = this.displayPassword === 'password' ? 'text' : 'password';
  }
}
